import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Navigate } from "react-router-dom";
import { MessageCircle, Loader2, Flag, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

export default function CommunityFeed() {
  const { user, loading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/community/posts"); setPosts(data); }
    catch (e) { if (e.response?.status !== 403) toast.error("Failed to load feed"); }
  };
  useEffect(() => { if (user?.vip || user?.role === "admin") load(); }, [user]);

  if (loading) return <Layout><div className="p-10">Loading...</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;

  if (!user.vip && user.role !== "admin") {
    return (
      <Layout>
        <div className="max-w-lg mx-auto p-10 text-center">
          <MessageCircle className="w-12 h-12 mx-auto text-pink mb-3" />
          <h1 className="font-display uppercase text-3xl">VIP Only Zone</h1>
          <p className="mt-3 text-muted">The community feed is exclusive to VIP members. Activate VIP from the /vip page.</p>
        </div>
      </Layout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim(); if (!t) return;
    setBusy(true);
    try { await api.post("/community/posts", { text: t }); setText(""); await load(); toast.success("Posted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const flag = async (id) => { await api.post(`/community/posts/${id}/flag`); toast.success("Reported"); };
  const del = async (id) => { if (!window.confirm("Delete this post?")) return; await api.delete(`/community/posts/${id}`); await load(); };

  return (
    <Layout>
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="chip bg-lime/10 text-lime mb-3">VIP Community</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl mb-6">The Insider Feed</h1>

        <form onSubmit={submit} className="glass rounded-3xl p-4 mb-6" data-testid="community-post-form">
          <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 500))} placeholder="Share a moment, drop a plan, hype an event…" rows={3} className="input-dark resize-none w-full" data-testid="community-post-input" />
          <div className="flex justify-between items-center mt-2">
            <span className="text-[10px] font-mono text-muted">{text.length}/500</span>
            <button disabled={busy || !text.trim()} type="submit" className="btn-primary !py-2 !px-4 !text-xs" data-testid="community-post-submit">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Post
            </button>
          </div>
        </form>

        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="glass rounded-3xl p-4" data-testid={`post-${p.id}`}>
              <div className="flex items-start gap-3">
                {p.user_picture ? <img src={p.user_picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-pink text-white flex items-center justify-center font-bold">{p.user_name?.[0]}</div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-sm">{p.user_name}</div>
                    <div className="text-[10px] font-mono text-muted">{new Date(p.created_at).toLocaleString()}</div>
                  </div>
                  <div className="mt-1 text-sm text-text/90 whitespace-pre-wrap">{p.text}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => flag(p.id)} data-testid={`post-flag-${p.id}`} className="icon-circle !w-8 !h-8 bg-white/5 text-muted hover:text-magenta"><Flag className="w-3.5 h-3.5" /></button>
                  {(user.role === "admin" || p.user_id === user.id) && <button onClick={() => del(p.id)} data-testid={`post-delete-${p.id}`} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            </div>
          ))}
          {posts.length === 0 && <div className="glass rounded-3xl p-6 text-center font-mono text-sm text-muted">Be the first to post</div>}
        </div>
      </section>
    </Layout>
  );
}
